import { normalizeIntroPhone } from '../sdk/intro-phone';
/**
 * demoLayout — pure, reactive layout decisions for the demo overlay (FLO-124).
 *
 * DemoView lays the video and the chat sidebar out as a flex *row*. That works
 * on a desktop, but on a phone a fixed-width sidebar beside the video crushes
 * the video (or calendar) pane to a sliver. These helpers centralise the
 * "what does this overlay state look like at mobile width" decisions so they
 * are (a) driven live by the reactive `useIsMobile()` value — not just the
 * initial mount — and (b) unit-testable without a DOM (the component itself
 * pulls in `marked`, which needs a browser, so the truth tables live here).
 *
 * Keep these PURE: they take `isMobile` (and friends) and return plain values.
 * The reactivity comes from the caller re-rendering when `useIsMobile()`
 * crosses the breakpoint on resize/rotate.
 */

import type { CSSProperties } from 'react';

/**
 * Stacking order for the end-call confirmation modal (the "Book Demo / End
 * Call" dialog). Shared so the mobile sidebar overlay can be pinned strictly
 * below it — the modal is a destructive-action gate and must always win.
 * DemoView applies this to the modal's container; keep them in lockstep.
 */
export const END_CONFIRM_MODAL_Z = 30;

/**
 * Stacking order for the chat sidebar when it overlays the canvas on mobile.
 * Must sit ABOVE the in-canvas overlays that share the left pane — the
 * loading / error / post-demo calendar surfaces all use `zIndex: 10` — and
 * above the gating intro form (`zIndex: 20`), so the open sidebar reads as the
 * foreground. It stays BELOW {@link END_CONFIRM_MODAL_Z} (30): the End demo
 * button lives in the open sidebar's control row, so its confirmation modal
 * must render on top of the sidebar, not behind it. (The collapsed-state
 * floating chrome at `zIndex: 100` is never rendered while the sidebar is
 * open, so it's not a binding constraint.)
 */
export const MOBILE_SIDEBAR_OVERLAY_Z = 25;

/**
 * Width of the desktop chat sidebar, in CSS px. DemoView passes this to
 * {@link sidebarContainerLayout}, and the AvatarPiP slot is carved out of it —
 * exported from here (not DemoView, which needs a DOM to import) so the layout
 * tests derive geometry from the real constant instead of a mirrored 360.
 */
export const SIDEBAR_WIDTH = 360;

/**
 * Layout for the OPEN chat sidebar.
 *
 * - Desktop: a fixed-width column that sits beside the video as a flex sibling
 *   (unchanged from the original layout).
 * - Mobile: a full-screen overlay above the video. A 360px sibling on a
 *   phone-width flex row would squeeze the video/calendar pane (`flex: 1`) down
 *   to a sliver; lifting the sidebar out of flow lets the video go full-bleed
 *   underneath and the chat take the whole screen — the standard mobile chat
 *   pattern.
 */
export function sidebarContainerLayout(isMobile: boolean, width: number): CSSProperties {
  if (isMobile) {
    return { position: 'absolute', inset: 0, width: '100%', zIndex: MOBILE_SIDEBAR_OVERLAY_Z };
  }
  return { width, flexShrink: 0 };
}

/**
 * Whether to auto-open the recap sidebar when the demo ends normally.
 *
 * - Desktop: yes. The recap (right) sits beside the booking calendar (left),
 *   so opening it doesn't hide the call-to-action.
 * - Mobile: no. The sidebar is now a full-screen overlay, so force-opening it
 *   would bury the booking calendar — the whole point of the post-demo state.
 *   The recap stays one tap away via the open-chat toggle.
 */
export function shouldAutoOpenPostDemoSidebar(showPostDemo: boolean, isMobile: boolean): boolean {
  return showPostDemo && !isMobile;
}

/**
 * Whether the sidebar header's right-edge button should CLOSE the whole demo
 * (vs. just collapse the sidebar).
 *
 * - Live demo (any width): collapse — it's the "hide chat" affordance.
 * - Post-demo desktop: close. The recap sits permanently beside the calendar,
 *   so there's nothing to collapse back to; the button is the exit (X).
 * - Post-demo mobile: collapse. The recap is a full-screen overlay ON TOP of
 *   the calendar, so the header button must return the prospect to the
 *   calendar. Exiting the demo entirely is handled by the labelled
 *   "Close demo" button at the foot of the recap.
 */
export function headerButtonClosesDemo(showPostDemo: boolean, isMobile: boolean): boolean {
  return showPostDemo && !isMobile;
}

/**
 * FLO-285: whether the demo overlay should render as the minimized bottom-docked
 * entry pill (vs. the full-bleed takeover).
 *
 * The pill is the low-friction entry for a marketing-site embed — it captures a
 * work email over the still-visible host page, then the demo goes full-bleed
 * once an email lands. It shows only when ALL of these hold:
 *
 *   - demoMode              — DemoView is a demo surface (always true today).
 *   - embedMode==='docked'  — dedicated demo pages pass 'fullscreen' to skip it.
 *   - showIntroForm         — we're still gating on the intro form...
 *   - !introFormSubmitted   — ...and it hasn't been completed.
 *   - !hasCapturedEmail     — the pill hasn't captured an email yet. Once it has,
 *                             the overlay goes full-bleed and never returns to the
 *                             pill for the rest of the session.
 *   - !hasPrefilledEmail    — the wrapper already supplied an email, so there is
 *                             nothing for the pill to collect — go straight to
 *                             full-bleed. (It has not started a session: the
 *                             prefill skips the ASK, not the cost gate.)
 *
 *   - !hasStarted           — the pill has already been used to start the demo.
 *                             Only reachable under a DEFERRED capture policy,
 *                             where the pill has no email field: every other
 *                             path leaves the docked state by capturing an
 *                             email, and `hasCapturedEmail` covers those. Its
 *                             absence was a real hang — the fieldless pill
 *                             booted a paid session and then kept rendering,
 *                             because nothing else in this predicate had moved.
 *
 * Deliberately NOT excluded: a deferred `identityCapture` on its own. The pill's
 * job is "enter the demo without a full-page takeover", and only its SECOND job
 * is collecting an email — so under a deferred policy it still shows, minus the
 * input (DemoDockedPill's `collectEmail` prop). Excluding it outright would
 * force a docked marketing embed into a full-viewport takeover the moment an
 * operator moved the ask later, which is the opposite of what they asked for.
 *
 * Pure + unit-tested (see demo-layout.test.ts) because these exclusions are easy
 * to regress and DemoView itself needs a DOM to render.
 */
export function shouldShowDockedPill(args: {
  demoMode: boolean;
  embedMode: 'docked' | 'fullscreen';
  showIntroForm: boolean;
  introFormSubmitted: boolean;
  hasCapturedEmail: boolean;
  hasPrefilledEmail: boolean;
  /** The pill's fieldless Start has already fired the cost gate. */
  hasStarted?: boolean;
}): boolean {
  return (
    args.demoMode &&
    args.embedMode === 'docked' &&
    args.showIntroForm &&
    !args.introFormSubmitted &&
    !args.hasCapturedEmail &&
    !args.hasPrefilledEmail &&
    !args.hasStarted
  );
}

// ─── Intro-form prefill (demo links that already know the prospect) ────────

/** Same shape the intro form collects. Mirrors the backend ProspectContext. */
export interface DemoIntroPrefill {
  phone?: string;
  email?: string;
  name?: string;
  company?: string;
  role?: string;
}

// ─── Identity-capture policy (WHEN the demo asks who the prospect is) ─────

/**
 * Mirrors floe-api's resolved `demoDiscovery.identityCapture`. The SDK receives
 * it from `validateClientKey` (site default) or, on a /d/{token} link, from the
 * resolved link (site ← link override, merged server-side).
 */
export interface DemoCapturePolicy {
  enabled: boolean;
  timing:
    | 'before_demo'
    | 'after_intro'
    | 'after_first_feature'
    | 'on_walkthrough_request'
    | 'on_end'
    | 'never';
  fields: Array<'email' | 'name' | 'phone' | 'company' | 'role'>;
  askFields?: Array<'email' | 'name' | 'phone' | 'company' | 'role'>;
  requiredFields?: Array<'email' | 'name' | 'phone' | 'company' | 'role'>;
  phoneRegion?: string;
  timingFallback?: { stored: 'on_end'; effective: 'never'; reason: 'ON_END_DISABLED' };
}

/** Today's behaviour, and what an un-configured/older floe-api resolves to. */
export const DEFAULT_CAPTURE_POLICY: DemoCapturePolicy = {
  enabled: true,
  timing: 'before_demo',
  fields: ['email', 'name'],
};

const CAPTURE_TIMINGS: ReadonlyArray<DemoCapturePolicy['timing']> = [
  'before_demo',
  'after_intro',
  'after_first_feature',
  'on_walkthrough_request',
  'on_end',
  'never',
];
const CAPTURE_FIELDS: ReadonlyArray<DemoCapturePolicy['fields'][number]> = [
  'email',
  'name',
  'phone',
  'company',
  'role',
];

/**
 * Narrow an untrusted `demoIdentityCapture` payload to a usable policy, or null.
 *
 * floe-api already whitelists this (resolveDiscovery), so this is the same
 * defensive second pass the SDK runs on `websiteAgent` and `suggested_options`
 * — but here the failure mode is asymmetric and worth naming: a malformed value
 * that resolved to "deferred" would swap the intro form for a fieldless Start
 * card and quietly stop capturing leads on a site that never asked for it. So
 * anything unrecognized returns NULL, which every caller reads as "no policy
 * served" → today's gating form. Unknown is never deferred.
 */
export function parseDemoCapturePolicy(raw: unknown): DemoCapturePolicy | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.enabled !== 'boolean') return null;
  if (
    typeof r.timing !== 'string' ||
    !CAPTURE_TIMINGS.includes(r.timing as DemoCapturePolicy['timing'])
  ) {
    return null;
  }
  const rawFields = r.askFields ?? r.fields;
  const fields = Array.isArray(rawFields)
    ? (rawFields.filter(
        (f): f is DemoCapturePolicy['fields'][number] =>
          typeof f === 'string' &&
          CAPTURE_FIELDS.includes(f as DemoCapturePolicy['fields'][number]),
      ) as DemoCapturePolicy['fields'])
    : [];
  return {
    enabled: r.enabled,
    timing: r.timing === 'on_end' ? 'never' : r.timing as DemoCapturePolicy['timing'],
    askFields: fields.length ? [...new Set(fields)] : [...DEFAULT_CAPTURE_POLICY.fields],
    requiredFields: Array.isArray(r.requiredFields) ? fields.filter(f => r.requiredFields && (r.requiredFields as unknown[]).includes(f)) : [],
    ...(typeof r.phoneRegion === 'string' && /^[A-Z]{2}$/.test(r.phoneRegion) ? { phoneRegion: r.phoneRegion } : {}),
    ...((r.timing === 'on_end' || (r.timingFallback && typeof r.timingFallback === 'object' && (r.timingFallback as Record<string, unknown>).reason === 'ON_END_DISABLED'))
      ? { timingFallback: { stored: 'on_end' as const, effective: 'never' as const, reason: 'ON_END_DISABLED' as const } } : {}),
    // A card with no inputs can't capture anything — fall back rather than
    // render a submit button over nothing.
    fields: fields.length > 0 ? fields : [...DEFAULT_CAPTURE_POLICY.fields],
  };
}

/** Is capture on at all? `enabled` and `timing` are independent — AND them. */
export function captureIsActive(capture: DemoCapturePolicy | undefined): boolean {
  const c = capture ?? DEFAULT_CAPTURE_POLICY;
  return c.enabled && c.timing !== 'never' && c.timing !== 'on_end';
}

/**
 * Does the intro gate collect anything, or is it a bare "Start demo" tap?
 *
 * `before_demo` (and an absent policy — an older floe-api that serves no
 * `demoIdentityCapture`) keeps the intro form. Everything else defers the ask
 * into the demo, which means the intro surface has nothing left to collect.
 *
 * ⚠️ It still renders a card, and that card still requires a TAP. Deferring the
 * ASK does not remove the GESTURE: nothing spawns a pipecat session at SDK
 * init, because headless link scanners (Proofpoint URL Defense, Defender Safe
 * Links) execute JS and would bill a session for every scanned share of a
 * /d/{token} link. A gesture is the one signal they can't emit by accident.
 * It is also what acquires the mic inside a transient user activation on iOS
 * (FLO-109). So: the form submit is the start signal under `before_demo`; the
 * Start tap is the start signal under every other timing.
 */
export function captureIsDeferred(capture: DemoCapturePolicy | undefined): boolean {
  if (!capture) return false; // no policy served → today's form
  return !captureIsActive(capture) || capture.timing !== 'before_demo';
}

export interface DemoIntroPlan {
  /**
   * Which surface the intro renders:
   *   'email' — today's Stage 1, "What's your work email?".
   *   'rest'  — Stage 2 (name + optional), email already known.
   *   'ready' — nothing left to ask; render the one-tap Start card.
   */
  stage: 'email' | 'rest' | 'ready';
  /** Seed values for the form fields. */
  values: Record<string, string>;
  /** True once the email is good enough to start the (paid) session. */
  emailReady: boolean;
  /**
   * Whether the Start button must hold out for an email + name.
   *
   * True on every `before_demo` path — including the prefilled 'ready' card,
   * whose button is only reachable because the link already supplied both.
   * FALSE when capture is deferred: there is nothing to validate, so gating the
   * button on `canStart` would render a Start card whose only control silently
   * does nothing. That is the sharp edge of this whole feature and the reason
   * this flag exists rather than being re-derived at the call site.
   */
  emailRequired: boolean;
}

/** Kept in lockstep with DemoView's EMAIL_RE and floe-site's PROSPECT_EMAIL_RE. */
const INTRO_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Decide what the demo's intro form should ask for, given whatever the host
 * page already knows about the prospect (a `/d/[token]` demo link carries
 * name/email/company/role; a curated demo page carries nothing).
 *
 * The point: a link minted WITH the prospect's details must not re-ask for
 * them. With both email and name we reach 'ready' — a single Start tap, no
 * typing. With only some of them we open at the furthest stage the data
 * supports and pre-fill the rest.
 *
 * Why 'ready' still costs one tap rather than auto-submitting: the tap IS the
 * start signal. Nothing spawns a session at SDK init, so this card is what
 * stands between a page load and a paid pipecat + avatar session.
 *
 *   - Cost: a page load is not intent. Headless link scanners (Proofpoint,
 *     Defender Safe Links) execute JS, so auto-submitting here would bill a
 *     session for every scanned share before a human opened it. A gesture is
 *     the one signal a crawler can't emit by accident — which is also why
 *     this is solved here rather than by trying to detect crawlers.
 *   - The mic: getUserMedia only prompts inside a transient user activation on
 *     iOS Safari (FLO-109). Starting from the tap acquires the mic on the same
 *     gesture; starting at init would have to connect mic-less and re-acquire
 *     later, which is the failure mode where the demo talks but never hears.
 *
 * The cost is a few seconds of browser warm-up that used to run under the
 * prospect's typing. On this path there is no typing left to hide it behind.
 *
 * An email that fails INTRO_EMAIL_RE is still seeded into the field (so a typo
 * is visible and correctable) but never counts as known — it can't advance the
 * stage, and it must not arm the cost gate.
 */
export function planDemoIntro(
  prefill: DemoIntroPrefill | undefined,
  capture?: DemoCapturePolicy,
): DemoIntroPlan {
  const clean = (v: string | undefined) => (typeof v === 'string' ? v.trim() : '');
  const email = clean(prefill?.email);
  const name = clean(prefill?.name);
  const company = clean(prefill?.company);
  const role = clean(prefill?.role);
  const phone = clean(prefill?.phone);

  const values: Record<string, string> = {};
  if (email) values.email = email;
  if (name) values.name = name;
  if (company) values.company = company;
  if (role) values.role = role;
  if (phone) values.phone = phone;

  const emailReady = !!email && INTRO_EMAIL_RE.test(email);

  // Capture deferred (or switched off): the intro has nothing to collect, so it
  // collapses to the Start card whatever the link did or didn't supply. A
  // prefilled email still counts as known — it seeds the profile and the Cal
  // prefill exactly as before — it just no longer decides which stage we open
  // at, because there are no stages left.
  if (captureIsDeferred(capture)) {
    return { stage: 'ready', values, emailReady, emailRequired: false };
  }

  // The before-demo form always requires email and name, even when a
  // conversational askFields policy selects only phone. Its configured
  // requiredFields add to that established form contract.
  const stage: DemoIntroPlan['stage'] = !emailReady
    ? 'email'
    : name && (capture?.requiredFields ?? []).every(f => f === 'phone'
        ? !!normalizeIntroPhone(values[f], capture?.phoneRegion)
        : !!values[f]?.trim())
      ? 'ready'
      : 'rest';

  return { stage, values, emailReady, emailRequired: true };
}

/**
 * The avatar PiP card (see ../components/AvatarPiP) never grows taller than
 * this, whatever shape the stream reports. The card sits ABOVE the brand
 * header, transcript and composer in the fixed-width chat sidebar — uncapped,
 * a portrait stream at the sidebar's full width would be ~500px tall and eat
 * the conversation.
 */
export const AVATAR_PIP_MAX_HEIGHT_PX = 280;

/** A video's intrinsic pixel size, as reported by `loadedmetadata`. */
export interface VideoDimensions {
  width: number;
  height: number;
}

/**
 * FLO-545 review: frame geometry for the avatar PiP card.
 *
 * The card used to hardcode `16 / 9`, but pipecat pins the Anam avatar stream
 * to 768×1152 PORTRAIT — `cover` inside a 16:9 frame crops that to ~37% of the
 * frame's height. So the frame adopts the stream's OWN ratio once metadata
 * reports it (null until then → the 16:9 the card has always had), with the
 * height capped so a portrait stream reads as a narrower, centered tile
 * instead of consuming the sidebar:
 *
 *   - `width: 100%` of the slot, but `maxWidth` = cap × ratio — whichever
 *     binds, height ≤ {@link AVATAR_PIP_MAX_HEIGHT_PX} and the box keeps the
 *     video's exact ratio, so `cover` has nothing left to crop.
 *   - 16:9 in DemoView's 360px sidebar: maxWidth (498) never binds →
 *     byte-identical to the old geometry.
 *   - 768×1152 portrait: a 187×280 tile, centered by the auto margins.
 *
 * Pure + unit-tested (demo-layout.test.ts) because AvatarPiP itself needs a
 * DOM to render — the metadata listeners live in the component, the geometry
 * they feed lives here.
 */
export function avatarPiPFrame(dims: VideoDimensions | null | undefined): CSSProperties {
  const valid =
    dims && Number.isFinite(dims.width) && Number.isFinite(dims.height) && dims.width > 0 && dims.height > 0
      ? dims
      : null;
  const w = valid ? valid.width : 16;
  const h = valid ? valid.height : 9;
  return {
    width: '100%',
    maxWidth: Math.round((AVATAR_PIP_MAX_HEIGHT_PX * w) / h),
    maxHeight: AVATAR_PIP_MAX_HEIGHT_PX,
    aspectRatio: `${w} / ${h}`,
    margin: '0 auto',
  };
}
